/**
 * Die Rückmeldung nach einer Buchung -- mit „Rückgängig“ (ADR 0013 §15).
 *
 * Rückgängig ist eine Gegenbuchung, kein Löschen: Das Log bleibt, wie es ist
 * (CLAUDE.md §3.2). Nach dem Zurücknehmen gibt es keinen zweiten Knopf -- wer
 * es sich wieder anders überlegt, scannt oder verschiebt neu.
 *
 * Nach „Alles da“, nach einer Aktion im Mehrfachscan und nach dem Ein- oder
 * Ausbuchen mit Auswahl (ADR 0020) nimmt „Rückgängig“ den ganzen Vorgang zurück.
 */

import { useCallback, useState, type ReactNode } from "react";
import type { Gebucht } from "../lokal/verschieben.js";
import { zuruecknehmen } from "../lokal/verschieben.js";
import { rueckmeldungGeben } from "../scan/Scanner.jsx";
import { Rueckmeldung, type MeldungsArt } from "./bausteine.jsx";

export interface Meldung {
  art: MeldungsArt;
  kopf: string;
  zusatz?: ReactNode;
  /** Gesetzt, wenn sich die Buchung -- oder der ganze Vorgang -- zurücknehmen lässt. */
  gebucht?: Gebucht | readonly Gebucht[];
}

function alsListe(gebucht: Gebucht | readonly Gebucht[]): readonly Gebucht[] {
  return "artikel" in gebucht ? [gebucht] : gebucht;
}

export function useBuchungsmeldung(nachZuruecknahme?: (gebucht: readonly Gebucht[]) => void): {
  meldung: Meldung | null;
  setMeldung: (m: Meldung | null) => void;
  anzeige: ReactNode;
} {
  const [meldung, setMeldung] = useState<Meldung | null>(null);

  const zurueck = useCallback(
    async (gebucht: readonly Gebucht[]): Promise<void> => {
      try {
        const anzahl = await zuruecknehmen(gebucht);
        const erster = gebucht[0];
        rueckmeldungGeben(true);
        setMeldung({
          art: "still",
          kopf: anzahl > 0 ? "↶ Rückgängig gemacht" : "Nichts zurückzunehmen",
          zusatz:
            gebucht.length === 1 && erster !== undefined
              ? `${erster.artikel.bezeichnung} · ${erster.artikel.inventarnummer}`
              : `${String(gebucht.length)} Artikel`,
        });
        nachZuruecknahme?.(gebucht);
      } catch (fehler) {
        rueckmeldungGeben(false);
        setMeldung({
          art: "warnung",
          kopf: "Nicht zurückgenommen",
          zusatz: fehler instanceof Error ? fehler.message : String(fehler),
        });
      }
    },
    [nachZuruecknahme],
  );

  const gebucht = meldung?.gebucht === undefined ? [] : alsListe(meldung.gebucht);
  const anzeige =
    meldung === null ? null : (
      <Rueckmeldung
        art={meldung.art}
        kopf={meldung.kopf}
        zusatz={meldung.zusatz}
        {...(gebucht.length === 0
          ? {}
          : {
              aktion: {
                text: "Rückgängig",
                tun: () => {
                  void zurueck(gebucht);
                },
              },
            })}
      />
    );

  return { meldung, setMeldung, anzeige };
}
